import {
  contextualActionsSchema,
  type ChatSource,
  type ContextMetrics,
  type ChatUsage,
  type ContextualAction,
} from "@base-hormonal/contracts";
import type { Database } from "@base-hormonal/database";
import type { TypedSupabaseClient } from "../../../shared/api/supabase";
import { fromSupabaseError, PublicAppError } from "../../../shared/lib/errors";
import { withRequestSignal } from "../../../shared/lib/request";

export type ConversationRow = Database["public"]["Tables"]["conversations"]["Row"];
export type MessageRow = Database["public"]["Tables"]["chat_messages"]["Row"];
export type DraftRow = Database["public"]["Tables"]["ai_drafts"]["Row"];

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  sources: ChatSource[];
  usage: ChatUsage | null;
  contextMetrics: ContextMetrics | null;
  attachmentName: string | null;
  createdAt: string;
  actions: ContextualAction[];
}

const CONVERSATION_COLUMNS =
  "id,user_id,aluno_id,title,memory_json,memory_updated_at,created_at,updated_at";
const MESSAGE_COLUMNS =
  "id,role,content,sources,contextual_actions,input_tokens,output_tokens,context_chars,context_estimated_tokens,context_domains,context_records,context_period_days,created_at";
const DRAFT_COLUMNS =
  "id,user_id,aluno_id,conversation_id,artifact_type,schema_version,payload_json,status,source_kind,source_template_id,model_id,input_tokens,output_tokens,approved_record_id,created_at,updated_at,expires_at,reviewed_at";

function raise(error: { code?: string } | null): void {
  const safe = fromSupabaseError(error);
  if (safe !== null) throw safe;
}

function toSources(value: unknown): ChatSource[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (item === null || typeof item !== "object") return [];
    const row = item as Record<string, unknown>;
    return [
      {
        source: typeof row.source === "string" ? row.source : null,
        text: typeof row.text === "string" ? row.text : "",
        score: typeof row.score === "number" ? row.score : null,
      },
    ];
  });
}

/** Projeção realmente selecionada de `chat_messages`. */
export type MessageProjection = Pick<
  MessageRow,
  | "id"
  | "role"
  | "content"
  | "sources"
  | "contextual_actions"
  | "input_tokens"
  | "output_tokens"
  | "context_chars"
  | "context_estimated_tokens"
  | "context_domains"
  | "context_records"
  | "context_period_days"
  | "created_at"
>;

export function toChatMessage(row: MessageProjection): ChatMessage {
  const parsedActions = contextualActionsSchema.safeParse(row.contextual_actions);
  return {
    id: row.id,
    role: row.role === "assistant" ? "assistant" : "user",
    content: row.content,
    sources: toSources(row.sources),
    usage:
      row.input_tokens !== null || row.output_tokens !== null
        ? { inputTokens: row.input_tokens ?? 0, outputTokens: row.output_tokens ?? 0 }
        : null,
    contextMetrics:
      row.context_chars !== null
        ? {
            chars: row.context_chars,
            estimatedTokens: row.context_estimated_tokens ?? 0,
            profileChars: 0,
            ragChars: 0,
            historyChars: 0,
            domains: row.context_domains ?? [],
            records: row.context_records ?? 0,
            periodDays: row.context_period_days,
          }
        : null,
    attachmentName: null,
    createdAt: row.created_at,
    actions: parsedActions.success ? parsedActions.data : [],
  };
}

export interface SaveMessageInput {
  id?: string;
  conversationId: string;
  role: "user" | "assistant";
  content: string;
  sources?: ChatSource[] | null;
  usage?: ChatUsage | null;
  contextMetrics?: ContextMetrics | null;
  actions?: ContextualAction[];
}

/**
 * Escritor único de conversas e mensagens a partir do cutover da Fase 3.
 * Toda autorização continua na RLS: aqui só existem consultas com o JWT do
 * usuário.
 */
export function createAssistantApi(client: TypedSupabaseClient) {
  return {
    async conversations(userId: string, signal?: AbortSignal): Promise<ConversationRow[]> {
      return withRequestSignal(signal, async (activeSignal) => {
        const { data, error } = await client
          .from("conversations")
          .select(CONVERSATION_COLUMNS)
          .eq("user_id", userId)
          .order("updated_at", { ascending: false })
          .abortSignal(activeSignal);
        raise(error);
        return data ?? [];
      });
    },

    async messages(
      conversationId: string,
      limit = 80,
      signal?: AbortSignal,
    ): Promise<ChatMessage[]> {
      return withRequestSignal(signal, async (activeSignal) => {
        const { data, error } = await client
          .from("chat_messages")
          .select(MESSAGE_COLUMNS)
          .eq("conversation_id", conversationId)
          .order("created_at", { ascending: false })
          .limit(limit)
          .abortSignal(activeSignal);
        raise(error);
        return (data ?? []).map(toChatMessage).reverse();
      });
    },

    async drafts(profileId: string, signal?: AbortSignal): Promise<DraftRow[]> {
      return withRequestSignal(signal, async (activeSignal) => {
        const { data, error } = await client
          .from("ai_drafts")
          .select(DRAFT_COLUMNS)
          .eq("aluno_id", profileId)
          .order("created_at", { ascending: false })
          .limit(50)
          .abortSignal(activeSignal);
        raise(error);
        return data ?? [];
      });
    },

    async createConversation(userId: string, profileId: string | null): Promise<ConversationRow> {
      return withRequestSignal(undefined, async (signal) => {
        const { data, error } = await client
          .from("conversations")
          .insert({ user_id: userId, title: "Nova conversa", aluno_id: profileId })
          .select(CONVERSATION_COLUMNS)
          .abortSignal(signal)
          .single();
        raise(error);
        if (data === null) throw new PublicAppError("empty_response", "A conversa não foi criada.");
        return data;
      });
    },

    async renameConversation(conversationId: string, title: string): Promise<void> {
      await withRequestSignal(undefined, async (signal) => {
        const { error } = await client
          .from("conversations")
          .update({ title: title.slice(0, 200) })
          .eq("id", conversationId)
          .abortSignal(signal);
        raise(error);
      });
    },

    async deleteConversation(conversationId: string): Promise<void> {
      await withRequestSignal(undefined, async (signal) => {
        const { error } = await client
          .from("conversations")
          .delete()
          .eq("id", conversationId)
          .abortSignal(signal);
        raise(error);
      });
    },

    async saveMessage(userId: string, input: SaveMessageInput): Promise<void> {
      await withRequestSignal(undefined, async (signal) => {
        const { error } = await client
          .from("chat_messages")
          .insert({
            ...(input.id === undefined ? {} : { id: input.id }),
            user_id: userId,
            conversation_id: input.conversationId,
            role: input.role,
            content: input.content,
            sources: input.sources ?? null,
            contextual_actions: input.actions ?? [],
            input_tokens: input.usage?.inputTokens ?? null,
            output_tokens: input.usage?.outputTokens ?? null,
            context_chars: input.contextMetrics?.chars ?? null,
            context_estimated_tokens: input.contextMetrics?.estimatedTokens ?? null,
            context_domains: input.contextMetrics?.domains ?? null,
            context_records: input.contextMetrics?.records ?? null,
            context_period_days: input.contextMetrics?.periodDays ?? null,
          })
          .abortSignal(signal);
        raise(error);
      });
      await withRequestSignal(undefined, async (signal) => {
        const { error } = await client
          .from("conversations")
          .update({ updated_at: new Date().toISOString() })
          .eq("id", input.conversationId)
          .abortSignal(signal);
        raise(error);
      });
    },

    async deleteMessage(messageId: string): Promise<void> {
      await withRequestSignal(undefined, async (signal) => {
        const { error } = await client
          .from("chat_messages")
          .delete()
          .eq("id", messageId)
          .abortSignal(signal);
        raise(error);
      });
    },
  };
}

export type AssistantApi = ReturnType<typeof createAssistantApi>;
